import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { api, API, LOGO } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';

const PALETTE = [
  ['Marsala', '#A94F4D', 'Pantone 7621 U', 'Il colore della voce'],
  ['Argilla', '#B08A74', 'Pantone 4715 U', 'L’imperfezione come unicità'],
  ['Moka', '#D6CDC0', 'Pantone 7527 U', 'Calore e accoglienza'],
  ['Azzurro estense', '#6E9AB0', 'Pantone 549 U', 'Libertà creativa'],
  ['Ocra', '#DFA23C', 'Pantone 7406 U', 'Energia e ironia'],
];

const KIT_URL = `${API}/press-kit/archive`;

/** Il pacchetto unico: lo ZIP è costruito e messo in cache dal backend. */
const BrandKit = () => {
  const { t, lang } = useLang();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer;
    const load = async () => {
      try {
        const r = await api.get('/press-kit/archive/info');
        if (!alive) return;
        setInfo(r.data);
        if (!r.data.ready) timer = setTimeout(load, 4000);
      } catch {
        /* se l'info non arriva, il download resta comunque raggiungibile */
      }
    };
    load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  const ready = info?.ready;
  const updated = info?.built_at
    ? new Date(info.built_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT')
    : null;

  return (
    <section
      data-testid="press-kit-package"
      className="grana bg-inchiostro py-14 text-carta md:py-20"
    >
      <div className="wrap grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
        <div>
          <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] leading-[1.05]">{t('press.kitTitle')}</h2>
          <p className="mt-7 max-w-[44ch] pt-6 text-base leading-relaxed text-carta/70">
            {t('press.kitLead')}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-6">
            {ready ? (
              <a
                href={KIT_URL}
                data-testid="press-kit-download"
                className="group inline-flex items-center gap-3 bg-carta px-7 py-4 text-[14px] font-bold text-inchiostro transition-transform duration-300 hover:-translate-y-0.5"
              >
                {t('press.kitDownload')}
                <Download size={14} strokeWidth={2.2} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            ) : (
              <span
                data-testid="press-kit-preparing"
                className="inline-flex items-center gap-3 border border-carta/30 px-7 py-4 text-[14px] font-bold text-carta/70"
              >
                <Loader2 size={14} strokeWidth={2.2} className="animate-spin" />
                {t('press.kitPreparing')}
              </span>
            )}
            <span data-testid="press-kit-size" className="text-[14px] font-bold text-carta/70">
              ZIP{info?.size ? ` · ${info.size}` : ''}
              {updated ? ` · ${t('press.kitUpdated')} ${updated}` : ''}
            </span>
          </div>
        </div>

        <ul className="self-end" data-testid="press-kit-contents">
          {t('press.kitContents').map((line, i) => (
            <li key={line} className="flex gap-5 py-5">
              <span className="text-base leading-relaxed text-carta/75">{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default function PressArea() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/press-kit?lang=${lang}`).then((r) => setItems(r.data));
  }, [lang]);

  return (
    <>
      <Seo title={t('press.title')} description={t('press.lead')} path="/area-stampa" />

      <header className="py-14 md:py-24">
        <div className="wrap">
          <h1 className="display-xl max-w-[14ch]">{t('press.title')}</h1>
          <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">{t('press.lead')}</p>
        </div>
      </header>

      <BrandKit />

      <section data-testid="press-downloads" className="py-14 md:py-20">
        <div className="wrap">
          <h2 className="mb-9 text-[24px] leading-[1.1] md:text-[36px]">{t('press.singleFiles')}</h2>
          <Stagger className="grid gap-px bg-inchiostro/12 md:grid-cols-3" gap={0.07}>
            {items.map((d) => (
              <StaggerItem key={d.slug} className="bg-carta p-7 md:p-9">
                <p className="label">
                  {d.format} · {d.size}
                </p>
                <h2 className="mt-3 font-display text-2xl">{d.title}</h2>
                <p className="prosa mt-3">{d.desc}</p>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`download-${d.slug}`}
                  className="btn btn-line mt-7"
                >
                  {t('press.download')}
                  <Download size={14} strokeWidth={2} />
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <Reveal>
            <p className="accento mb-7 text-2xl text-inchiostro/85">Logo</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid aspect-square place-items-center bg-carta-2 p-6">
                <img src={LOGO.full} alt="Logo VdE con pennellata" className="w-full" />
              </div>
              <div className="grid aspect-square place-items-center bg-inchiostro p-6">
                <img
                  src={LOGO.essential}
                  alt="Logo VdE versione essenziale"
                  className="w-full brightness-0 invert-[0.92] sepia-[0.2] saturate-[3] hue-rotate-[-10deg]"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mb-7 text-sm text-inchiostro/65">{t('press.rulesTitle')}</p>
            <dl>
              {t('press.rules').map(([k, v]) => (
                <div key={k} className="py-5">
                  <dt className="font-display text-xl">{k}</dt>
                  <dd className="prosa mt-2">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section data-testid="press-palette" className="bg-carta-2 py-14 md:py-20">
        <div className="wrap">
          <p className="accento mb-9 text-2xl text-inchiostro/85">{t('press.paletteTitle')}</p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {PALETTE.map(([name, hex, pantone, meaning]) => (
              <div key={name}>
                <div className="h-28 md:h-40" style={{ backgroundColor: hex }} />
                <h3 className="mt-4 font-display text-xl">{name}</h3>
                <p className="mt-1 font-sans text-[14px] text-inchiostro/70">
                  {hex} · {pantone}
                </p>
                <p className="mt-2 text-sm text-inchiostro/65">{meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <h2 className="mb-7 text-[24px] leading-[1.1] md:text-[36px]">{t('press.typeTitle')}</h2>
            <p className="font-display text-6xl md:text-8xl">Aa</p>
            <p className="mt-3 text-[14px] font-semibold text-inchiostro/70">
              Bodoni Moda, display (Didot)
            </p>
            <p className="mt-10 text-6xl font-semibold md:text-8xl">Aa</p>
            <p className="mt-3 text-[14px] font-semibold text-inchiostro/70">
              Archivo, testo (Helvetica)
            </p>
          </div>
          <div className="lg:pt-14">
            <p className="prosa">{t('press.typeText')}</p>
            <p className="mt-9 font-display text-3xl italic text-marsala md:text-4xl">
              Vivi di emozioni
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
